import { useState } from 'react';
import { Download, ExternalLink, Trash2 } from 'lucide-react';
import { filesApi } from '@/lib/api';
import { formatBytes, formatDuration, tsLocal } from '@/lib/format';
import type { FileMeta } from '@/lib/types';
import clsx from 'clsx';

interface Props {
  items: FileMeta[];
  onOpen?: (file: FileMeta) => void;
  onChanged?: () => void;
  /** When true, render images in shots kind as a thumbnail grid instead. */
  thumbnails?: boolean;
}

export function FilesTable({ items, onOpen, onChanged, thumbnails }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const allSelected = selected.size > 0 && selected.size === items.length;
  const toggle = (p: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === items.length ? new Set() : new Set(items.map((i) => i.path))));
  };
  const onDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} file(s)?`)) return;
    await filesApi.delete([...selected]);
    setSelected(new Set());
    onChanged?.();
  };

  if (thumbnails) {
    return (
      <div>
        {selected.size > 0 && (
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs text-ink-muted">{selected.size} selected</span>
            <button className="btn" onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.map((it) => {
            const checked = selected.has(it.path);
            const isImage = it.kind === 'screenshots';
            const isVideo = it.kind === 'recordings';
            return (
              <div
                key={it.path}
                className={clsx(
                  'card overflow-hidden ring-2 ring-transparent',
                  checked && 'ring-accent'
                )}
              >
                <button
                  className="block w-full"
                  onClick={() => onOpen?.(it)}
                  aria-label={`open ${it.name}`}
                >
                  {isImage ? (
                    <img
                      src={filesApi.rawUrl(it.path)}
                      alt={it.name}
                      className="aspect-[9/16] h-auto w-full bg-bg-soft object-contain"
                      loading="lazy"
                    />
                  ) : isVideo ? (
                    <div className="flex aspect-video w-full items-center justify-center bg-bg-soft text-xs text-ink-muted">
                      MP4 · {formatDuration(it.extra?.durationSec)}
                    </div>
                  ) : (
                    <div className="flex aspect-[4/3] w-full items-center justify-center bg-bg-soft text-xs text-ink-muted">
                      {it.kind}
                    </div>
                  )}
                </button>
                <div className="space-y-1 p-2">
                  <div className="flex items-center justify-between gap-2 text-[11px] text-ink-muted">
                    <label className="inline-flex cursor-pointer items-center gap-1">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(it.path)}
                      />
                      <span className="font-mono">{it.name}</span>
                    </label>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-ink-dim">
                    <span>
                      {it.extra?.width
                        ? `${it.extra.width}×${it.extra.height}`
                        : tsLocal(it.mtime).slice(5, 16)}
                    </span>
                    <span>{formatBytes(it.bytes)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {items.length === 0 && (
          <p className="py-8 text-center text-sm text-ink-muted">No files yet.</p>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-line">
      <div className="flex items-center justify-between border-b border-line bg-bg-soft px-3 py-2 text-xs text-ink-muted">
        <label className="flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} />
          <span>{items.length} files{selected.size > 0 ? ` · ${selected.size} selected` : ''}</span>
        </label>
        {selected.size > 0 && (
          <button className="btn" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" /> Delete selected
          </button>
        )}
      </div>
      <div className="max-h-[60vh] overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-bg-card">
            <tr className="text-[11px] uppercase tracking-wide text-ink-muted">
              <th className="w-8 px-3 py-2"></th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2 text-right">Size</th>
              <th className="px-3 py-2">Modified</th>
              <th className="px-3 py-2">Extra</th>
              <th className="w-32 px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const checked = selected.has(it.path);
              return (
                <tr key={it.path} className={clsx('border-t border-line hover:bg-bg-soft/60', checked && 'bg-bg-soft/60')}>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={checked} onChange={() => toggle(it.path)} />
                  </td>
                  <td className="px-3 py-2">
                    <button className="text-left text-ink hover:text-accent" onClick={() => onOpen?.(it)}>
                      <span className="font-mono text-xs">{it.name}</span>
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <span className="badge">{it.kind}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatBytes(it.bytes)}</td>
                  <td className="px-3 py-2 text-ink-muted">{tsLocal(it.mtime)}</td>
                  <td className="px-3 py-2 text-xs text-ink-muted">
                    {it.kind === 'screenshots' && it.extra?.width
                      ? `${it.extra.width}×${it.extra.height}`
                      : it.kind === 'recordings'
                        ? formatDuration(it.extra?.durationSec)
                        : it.kind === 'logcat' && it.extra?.lines !== undefined
                          ? `${it.extra.lines.toLocaleString()} lines`
                          : '-'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a
                      className="btn-ghost"
                      href={filesApi.rawUrl(it.path, true)}
                      title="Download"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </a>
                    <a
                      className="btn-ghost"
                      href={filesApi.rawUrl(it.path)}
                      target="_blank"
                      rel="noreferrer"
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="py-8 text-center text-sm text-ink-muted">
                  No files yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
